import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/Button';
import { Mail, CheckCircle, Loader2, Calendar, FileText, CheckSquare, Square, Upload, Server } from 'lucide-react';
import { useNotifications } from 'contexts/NotificationContext';
import { uploadsApi } from 'lib/api';

interface EmailImportViewProps {
  selectedTeam: string;
  onImportStarted: (batchSummary: any) => void;
  disabled?: boolean;
}

interface FetchedPdf {
  id: string;
  filename: string;
  subject: string;
  date: string;
  sender: string;
  is_duplicate?: boolean;
  is_application?: boolean;
}

type EmailProvider = 'gmail' | 'imap' | 'outlook' | 'exchange';

interface ImapConnectionConfig {
  host: string;
  port: number;
  username: string;
  password: string;
  folder: string;
  use_ssl: boolean;
}

const defaultImapConfig: ImapConnectionConfig = {
  host: '',
  port: 993,
  username: '',
  password: '',
  folder: 'INBOX',
  use_ssl: true,
};

export function EmailImportView({ selectedTeam, onImportStarted, disabled }: EmailImportViewProps) {
  const { t } = useTranslation();
  const { addNotification } = useNotifications();

  const [selectedProvider, setSelectedProvider] = useState<EmailProvider>('gmail');
  const [connectedProvider, setConnectedProvider] = useState<EmailProvider | null>(null);
  const [isConnectingGmail, setIsConnectingGmail] = useState(false);
  const [isConnectingImap, setIsConnectingImap] = useState(false);
  const [imapConfig, setImapConfig] = useState<ImapConnectionConfig>(defaultImapConfig);

  const [daysToFetch, setDaysToFetch] = useState<number>(7);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchedPdfs, setFetchedPdfs] = useState<FetchedPdf[]>([]);
  const [selectedPdfIds, setSelectedPdfIds] = useState<Set<string>>(new Set());
  const [hasFetched, setHasFetched] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [rememberConnection, setRememberConnection] = useState(false);

  const isConnected = connectedProvider !== null;
  const isConnecting = isConnectingGmail || isConnectingImap;

  const isImapFormValid = useMemo(() => {
    if (selectedProvider === 'outlook') {
      return Boolean(imapConfig.username.trim() && imapConfig.password.trim());
    }
    return Boolean(imapConfig.host.trim() && imapConfig.username.trim() && imapConfig.password.trim());
  }, [imapConfig.host, imapConfig.password, imapConfig.username, selectedProvider]);

  const resetFetchedState = () => {
    setFetchedPdfs([]);
    setSelectedPdfIds(new Set());
    setHasFetched(false);
  };

  useEffect(() => {
    const savedLocal = localStorage.getItem('selektia_email_connection');
    if (savedLocal) {
      try {
        const parsed = JSON.parse(savedLocal);
        if (parsed.remember) {
          setConnectedProvider(parsed.provider as EmailProvider);
        } else if (parsed.expiry && Date.now() < parsed.expiry) {
          setConnectedProvider(parsed.provider as EmailProvider);
        } else {
          localStorage.removeItem('selektia_email_connection');
        }
      } catch (e) {
        localStorage.removeItem('selektia_email_connection');
      }
    }
  }, []);

  const storeConnection = React.useCallback((provider: EmailProvider, email?: string) => {
    if (rememberConnection) {
      localStorage.setItem('selektia_email_connection', JSON.stringify({
        provider,
        email,
        remember: true
      }));
    } else {
      localStorage.setItem('selektia_email_connection', JSON.stringify({
        provider,
        email,
        expiry: Date.now() + 3600 * 1000 // 1 hour
      }));
    }
  }, [rememberConnection]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'gmail_auth_success') {
        storeConnection('gmail', event.data?.email);
        setConnectedProvider('gmail');
        setIsConnectingGmail(false);
        addNotification({
          type: 'success',
          title: t('common.success', 'Success'),
          message: t('candidates.emailImport.connectedSuccess', 'Successfully connected to Gmail'),
          category: 'system'
        });
      }

      if (event.data?.type === 'gmail_auth_error') {
        setIsConnectingGmail(false);
        addNotification({
          type: 'error',
          title: t('common.error', 'Error'),
          message: t('candidates.emailImport.connectFailed', 'Failed to connect to Gmail.'),
          category: 'system'
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [addNotification, t, rememberConnection, storeConnection]);

  const handleConnectGmail = async () => {
    try {
      setIsConnectingGmail(true);
      const response = await uploadsApi.getGmailAuthUrl();
      const authUrl = response.data.auth_url;

      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        authUrl,
        'Gmail Auth',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      const checkPopup = setInterval(() => {
        if (!popup || popup.closed) {
          clearInterval(checkPopup);
          setIsConnectingGmail(false);
        }
      }, 500);
    } catch (error) {
      console.error('Failed to get Gmail auth URL', error);
      setIsConnectingGmail(false);
      addNotification({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('candidates.emailImport.connectFailed', 'Failed to connect to Gmail.'),
        category: 'system'
      });
    }
  };

  const handleConnectImap = async () => {
    if (!isImapFormValid) {
      addNotification({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('candidates.emailImport.imapMissingFields', 'Please fill host, username and password.'),
        category: 'system'
      });
      return;
    }

    try {
      setIsConnectingImap(true);
      
      if (selectedProvider === 'outlook') {
        const response = await uploadsApi.connectOutlookMailbox({
          username: imapConfig.username.trim(),
          password: imapConfig.password,
          folder: imapConfig.folder.trim() || 'INBOX',
        });
        
        storeConnection('outlook', response.data?.email || imapConfig.username.trim());
        setConnectedProvider('outlook');
        addNotification({
          type: 'success',
          title: t('common.success', 'Success'),
          message: 'Connecté avec Outlook/Exchange avec succès',
          category: 'system'
        });
        return;
      }
      
      const response = await uploadsApi.connectImapMailbox({
        host: imapConfig.host.trim(),
        port: Number(imapConfig.port) || 993,
        username: imapConfig.username.trim(),
        password: imapConfig.password,
        folder: imapConfig.folder.trim() || 'INBOX',
        use_ssl: imapConfig.use_ssl,
      });

      storeConnection('imap', response.data?.email || imapConfig.username.trim());
      setConnectedProvider('imap');
      addNotification({
        type: 'success',
        title: t('common.success', 'Success'),
        message: t('candidates.emailImport.imapConnectedSuccess', 'Successfully connected to IMAP mailbox.'),
        category: 'system'
      });
    } catch (error) {
      console.error('Failed to connect IMAP mailbox', error);
      addNotification({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('candidates.emailImport.imapConnectFailed', 'Failed to connect to IMAP mailbox.'),
        category: 'system'
      });
    } finally {
      setIsConnectingImap(false);
    }
  };

  const handleDisconnect = () => {
    localStorage.removeItem('selektia_email_connection');
    localStorage.removeItem('selektia_auto_sync_active');
    localStorage.removeItem('selektia_auto_sync_interval');
      localStorage.removeItem('selektia_auto_sync_pending');
      localStorage.removeItem('selektia_auto_sync_last');
    
    setConnectedProvider(null);
    setIsConnectingGmail(false);
    setIsConnectingImap(false);
    resetFetchedState();
  };

  const handleFetchPdfs = async () => {
    if (!connectedProvider) return;

    try {
      setIsFetching(true);
      setHasFetched(false);

      const response = (connectedProvider === 'imap' || connectedProvider === 'outlook' || connectedProvider === 'exchange')
        ? await uploadsApi.fetchImapPdfs(daysToFetch)
        : await uploadsApi.fetchGmailPdfs(daysToFetch);

      const files = response.data.pdfs || [];
      setFetchedPdfs(files);
      setHasFetched(true);
      // By default, select files that are neither a duplicate, nor explicitly NOT an application
      setSelectedPdfIds(new Set(
        files
          .filter((file: FetchedPdf) => !file.is_duplicate && file.is_application !== false)
          .map((file: FetchedPdf) => file.id)
      ));
    } catch (error) {
      console.error('Failed to fetch PDFs', error);
      addNotification({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('candidates.emailImport.fetchFailed', 'Failed to fetch files from mailbox.'),
        category: 'system'
      });
    } finally {
      setIsFetching(false);
    }
  };

  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedPdfIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedPdfIds(newSelected);
  };

  const selectAll = () => {
    setSelectedPdfIds(new Set(
      fetchedPdfs
        .filter(pdf => !pdf.is_duplicate && pdf.is_application !== false)
        .map(pdf => pdf.id)
    ));
  };

  const deselectAll = () => {
    setSelectedPdfIds(new Set());
  };

  const handleImport = async () => {
    if (selectedPdfIds.size === 0 || !connectedProvider) return;

    try {
      setIsImporting(true);

      const selectedFiles = fetchedPdfs
        .filter(pdf => selectedPdfIds.has(pdf.id))
        .map(pdf => ({
          id: pdf.id,
          filename: pdf.filename
        }));

      const response = (connectedProvider === 'imap' || connectedProvider === 'outlook' || connectedProvider === 'exchange')
        ? await uploadsApi.importImapPdfs({ files: selectedFiles, team_id: selectedTeam || null })
        : await uploadsApi.importGmailPdfs({ files: selectedFiles, team_id: selectedTeam || null });

      onImportStarted({
        batchId: response.data.batch_id,
        batchName: response.data.batch_name,
        totalFiles: response.data.total_files
      });
    } catch (error) {
      console.error('Import failed', error);
      addNotification({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('candidates.emailImport.importFailed', 'Failed to import selected files.'),
        category: 'cv'
      });
    } finally {
      setIsImporting(false);
    }
  };

  if (!isConnected) {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => setSelectedProvider('gmail')}
            className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${selectedProvider === 'gmail' ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background text-foreground hover:bg-muted/50'}`}
          >
            <Mail className="h-4 w-4" />
            Google Workspace
          </button>
          <button
            type="button"
            onClick={() => setSelectedProvider('outlook')}
            className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${selectedProvider === 'outlook' ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background text-foreground hover:bg-muted/50'}`}
          >
            <Server className="h-4 w-4" />
            Exchange / Outlook
          </button>
          <button
            type="button"
            onClick={() => setSelectedProvider('imap')}
            className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${selectedProvider === 'imap' ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background text-foreground hover:bg-muted/50'}`}
          >
            <Server className="h-4 w-4" />
            {t('candidates.emailImport.providerImap', 'Boîte pro (IMAP)')}
          </button>
        </div>

        {selectedProvider === 'gmail' ? (
          <div className="flex flex-col items-center justify-center p-8 space-y-4 border-2 border-dashed border-border rounded-lg bg-muted/30">
            <Mail className="h-12 w-12 text-muted-foreground" />
            <div className="text-center space-y-1">
              <p className="text-sm font-medium text-foreground">
                {t('candidates.emailImport.title', 'Import from Gmail')}
              </p>
              <p className="text-xs text-muted-foreground w-3/4 mx-auto">
                {t('candidates.emailImport.description', 'Connect your Gmail account to retrieve CV files from your emails and add them as candidates.')}
              </p>
            </div>
            <Button
              type="button"
              onClick={handleConnectGmail}
              disabled={isConnecting || disabled}
              className="mt-4"
            >
              {isConnectingGmail ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
              {t('candidates.emailImport.connectButton', 'Connect Google Account')}
            </Button>
          </div>
        ) : (
          <div className="space-y-4 p-4 border rounded-lg bg-card">
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">
                {t('candidates.emailImport.imapTitle', 'Connect professional mailbox (IMAP)')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('candidates.emailImport.imapDescription', 'Use your mail server settings to fetch CV attachments.')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {selectedProvider !== 'outlook' && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-foreground">
                      {t('candidates.emailImport.imapHost', 'IMAP host')}
                    </label>
                    <input
                      type="text"
                      value={imapConfig.host}
                      onChange={(event) => setImapConfig(previous => ({ ...previous, host: event.target.value }))}
                      disabled={isConnecting || disabled}
                      placeholder="imap.company.com"
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-foreground">
                      {t('candidates.emailImport.imapPort', 'Port')}
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={65535}
                      value={imapConfig.port}
                      onChange={(event) => setImapConfig(previous => ({ ...previous, port: Number(event.target.value) || 993 }))}
                      disabled={isConnecting || disabled}
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>
                </>
              )}

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">
                  {t('candidates.emailImport.imapUsername', 'Email / username')}
                </label>
                <input
                  type="text"
                  value={imapConfig.username}
                  onChange={(event) => setImapConfig(previous => ({ ...previous, username: event.target.value }))}
                  disabled={isConnecting || disabled}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">
                  {t('candidates.emailImport.imapPassword', 'Password / App password')}
                </label>
                <input
                  type="password"
                  value={imapConfig.password}
                  onChange={(event) => setImapConfig(previous => ({ ...previous, password: event.target.value }))}
                  disabled={isConnecting || disabled}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">
                  {t('candidates.emailImport.imapFolder', 'Folder')}
                </label>
                <input
                  type="text"
                  value={imapConfig.folder}
                  onChange={(event) => setImapConfig(previous => ({ ...previous, folder: event.target.value }))}
                  disabled={isConnecting || disabled}
                  placeholder="INBOX"
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              {selectedProvider !== 'outlook' && (
                <label className="flex items-center gap-2 mt-6 text-xs font-medium text-foreground">
                  <input
                    type="checkbox"
                    checked={imapConfig.use_ssl}
                    onChange={(event) => setImapConfig(previous => ({ ...previous, use_ssl: event.target.checked }))}
                    disabled={isConnecting || disabled}
                    className="h-4 w-4 rounded border-input"
                  />
                  {t('candidates.emailImport.imapUseSsl', 'Use SSL/TLS')}
                </label>
              )}
            </div>

            <div className="flex justify-end">
              <Button
                type="button"
                onClick={handleConnectImap}
                disabled={!isImapFormValid || isConnecting || disabled}
              >
                {isConnectingImap ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Server className="mr-2 h-4 w-4" />}
                {selectedProvider === 'outlook' ? 'Connecter Exchange/Outlook' : t('candidates.emailImport.imapConnectButton', 'Connect IMAP Mailbox')}
              </Button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-center mt-2">
          <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={rememberConnection}
              onChange={(e) => setRememberConnection(e.target.checked)}
              disabled={isConnecting || disabled}
              className="h-4 w-4 rounded border-input"
            />
            {t('candidates.emailImport.rememberConnection', 'Se souvenir de la connexion')}
          </label>
        </div>
      </div>
    );
  }

  const connectedLabel = connectedProvider === 'imap'
    ? t('candidates.emailImport.connectedImap', 'Connected to IMAP mailbox')
    : t('candidates.emailImport.connected', 'Connected to Gmail');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between bg-emerald-50 dark:bg-emerald-900/20 p-3 rounded-lg border border-emerald-200 dark:border-emerald-800">
        <div className="flex items-center gap-2 text-sm text-emerald-800 dark:text-emerald-300">
          <CheckCircle className="h-4 w-4" />
          <span>{connectedLabel}</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          type="button"
          onClick={handleDisconnect}
          className="text-xs h-7"
        >
          {t('candidates.emailImport.changeAccount', 'Change Account')}
        </Button>
      </div>

      <div className="flex items-end gap-3 p-4 border rounded-lg bg-card">
        <div className="space-y-1.5 flex-1">
          <label className="text-xs font-medium text-foreground flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            {t('candidates.emailImport.period', 'Search period (last X days)')}
          </label>
          <input
            type="number"
            min={1}
            max={30}
            value={daysToFetch}
            onChange={(event) => setDaysToFetch(Number(event.target.value) || 7)}
            disabled={isFetching || isImporting || disabled}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>
        <Button
          type="button"
          disabled={isFetching || isImporting || disabled}
          onClick={handleFetchPdfs}
        >
          {isFetching ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : connectedProvider === 'imap' ? (
            <Server className="mr-2 h-4 w-4" />
          ) : (
            <Mail className="mr-2 h-4 w-4" />
          )}
          {t('candidates.emailImport.fetchPdfs', 'Search files')}
        </Button>
      </div>

      {hasFetched && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-foreground">
              {t('candidates.emailImport.foundResults', { count: fetchedPdfs.length, defaultValue: 'Found {{count}} files' })}
            </h4>
            {fetchedPdfs.length > 0 && (
              <div className="flex space-x-2 text-xs">
                <button type="button" onClick={selectAll} className="text-primary hover:underline">
                  {t('candidates.emailImport.selectAll', 'Select all')}
                </button>
                <span className="text-muted-foreground">|</span>
                <button type="button" onClick={deselectAll} className="text-primary hover:underline">
                  {t('candidates.emailImport.deselectAll', 'Deselect all')}
                </button>
              </div>
            )}
          </div>

          {fetchedPdfs.length === 0 ? (
            <div className="p-6 border border-dashed rounded-lg text-center text-sm text-muted-foreground">
              {t('candidates.emailImport.noPdfs', 'No file attachments found in this period.')}
            </div>
          ) : (
            <div className="border rounded-lg max-h-64 overflow-y-auto divide-y">
              {fetchedPdfs.map(pdf => {
                const isSelected = selectedPdfIds.has(pdf.id);
                const isNotApplication = pdf.is_application === false;
                const isOpacified = pdf.is_duplicate || isNotApplication;
                
                return (
                  <div
                    key={pdf.id}
                    className={`flex items-start gap-3 p-3 transition-colors cursor-pointer ${isSelected ? 'bg-primary/5' : 'hover:bg-muted/50'} ${isOpacified ? 'opacity-60 bg-muted/30' : ''}`}
                    onClick={() => toggleSelection(pdf.id)}
                  >
                    <div className="pt-0.5">
                      {isSelected ? <CheckSquare className="h-4 w-4 text-primary" /> : <Square className="h-4 w-4 text-muted-foreground" />}
                    </div>
                    <FileText className={`h-5 w-5 shrink-0 ${isOpacified ? 'text-red-300' : 'text-red-500'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate flex items-center gap-2">
                        {pdf.filename}
                        {pdf.is_duplicate && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                            {t('candidates.emailImport.duplicate', 'Dupliqué')}
                          </span>
                        )}
                        {!pdf.is_duplicate && isNotApplication && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                            {t('candidates.emailImport.notApplication', 'E-mail normal')}
                          </span>
                        )}
                      </p>
                      <div className="flex justify-between items-center text-xs text-muted-foreground mt-0.5">
                        <span className="truncate mr-2">
                          {t('candidates.emailImport.from', 'From:')} {pdf.sender} • {t('candidates.emailImport.subject', 'Subj:')} {pdf.subject}
                        </span>
                        <span className="shrink-0">{pdf.date}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {fetchedPdfs.length > 0 && (
            <div className="flex justify-end pt-2">
              <Button
                type="button"
                disabled={selectedPdfIds.size === 0 || isImporting || disabled}
                onClick={handleImport}
              >
                {isImporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                {t('candidates.emailImport.importSelected', { count: selectedPdfIds.size, defaultValue: 'Import Selected ({{count}})' })}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}